import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PatientHeader from '../components/patient/PatientHeader';
import PatientSidebar from '../components/patient/PatientSidebar';
import NewRequestModal from '../components/patient/NewRequestModal';
import VisitDetailsModal from '../components/patient/VisitDetailsModal';
import CancelVisitModal from '../components/patient/CancelVisitModal';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import { mapVisitFromApi, VISIT_STATUS_CLASS } from '../Utils/patientVisits';
import { toast } from 'react-toastify';
import '../App.css';

const PLACEHOLDER_VALUES = new Set(['none', 'n/a', 'na', '-', '—']);

function emptyIfPlaceholder(value) {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return PLACEHOLDER_VALUES.has(trimmed.toLowerCase()) ? '' : trimmed;
}

function profileToForm(profile) {
  if (!profile) return null;
  return {
    fullName: emptyIfPlaceholder(profile.fullName),
    age: profile.age != null ? String(profile.age) : '',
    gender: emptyIfPlaceholder(profile.gender),
    mobileNumber: emptyIfPlaceholder(profile.mobileNumber),
    email: emptyIfPlaceholder(profile.email),
    pinCode: emptyIfPlaceholder(profile.pinCode),
    addressLine: emptyIfPlaceholder(profile.addressLine),
    landmark: emptyIfPlaceholder(profile.landmark),
    emergencyContactName: emptyIfPlaceholder(profile.emergencyContactName),
    emergencyContactPhoneNumber: emptyIfPlaceholder(profile.emergencyContactPhoneNumber),
    knownAllergies: emptyIfPlaceholder(profile.knownAllergies),
    medicalHistory: emptyIfPlaceholder(profile.medicalHistory),
    currentMedications: emptyIfPlaceholder(profile.currentMedications),
  };
}

function ProfileLabel({ htmlFor, required, children }) {
  return (
    <label htmlFor={htmlFor} className="form-label">
      {children}
      {required ? <span className="text-danger"> *</span> : null}
    </label>
  );
}

function PatientDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activePage, setActivePage] = useState('myVisits');
  const [visits, setVisits] = useState([]);
  const [isLoadingVisits, setIsLoadingVisits] = useState(false);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [viewVisit, setViewVisit] = useState(null);
  const [cancelVisit, setCancelVisit] = useState(null);
  const [isCancellingVisit, setIsCancellingVisit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [patientProfile, setPatientProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [profileDistrictId, setProfileDistrictId] = useState(null);
  const [profileDistrictName, setProfileDistrictName] = useState('');
  const [profilePincodeError, setProfilePincodeError] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isValidatingProfilePincode, setIsValidatingProfilePincode] = useState(false);
  const [originalProfilePincode, setOriginalProfilePincode] = useState('');
  const [isProfileEditMode, setIsProfileEditMode] = useState(false);
  const userName = patientProfile?.fullName || user?.displayName || 'Patient';

  useEffect(() => {
    if (activePage !== 'profile') {
      setIsProfileEditMode(false);
    }
  }, [activePage]);

  const loadVisits = useCallback(async () => {
    if (!user?.patientId) {
      setVisits([]);
      return;
    }

    setIsLoadingVisits(true);
    try {
      const response = await client.get(`/api/visit-requests/patient/${user.patientId}`);
      const { success, data, message } = response.data || {};
      if (success && Array.isArray(data)) {
        setVisits(data.map(mapVisitFromApi));
      } else {
        toast.error(message || 'Unable to load visit requests.');
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(apiMessage || 'Unable to load visit requests.');
    } finally {
      setIsLoadingVisits(false);
    }
  }, [user?.patientId]);

  useEffect(() => {
    if (activePage === 'myVisits' || activePage === 'newRequest') {
      loadVisits();
    }
  }, [activePage, loadVisits]);

  useEffect(() => {
    const loadProfile = async () => {
      if (activePage !== 'profile') return;

      if (!user?.patientId) {
        setPatientProfile(null);
        return;
      }

      setIsLoadingProfile(true);
      try {
        const response = await client.get(`/api/patients/${user.patientId}`);
        const { success, data, message } = response.data || {};
        if (success && data) {
          setPatientProfile(data);
          setProfileForm(profileToForm(data));
          setProfileDistrictId(data.districtID ?? data.districtId ?? null);
          setProfileDistrictName(data.districtName || '');
          setOriginalProfilePincode(emptyIfPlaceholder(data.pinCode));
        } else {
          toast.error(message || 'Unable to load profile.');
        }
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        toast.error(apiMessage || 'Unable to load profile.');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [activePage, user?.patientId]);

  const filteredVisits = useMemo(() => {
    return visits.filter((v) => {
      const matchesSearch =
        !searchQuery ||
        v.serviceType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.preferredDate.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [visits, searchQuery, statusFilter]);

  const openNewRequest = () => {
    setEditVisit(null);
    setActivePage('newRequest');
    setShowNewRequestModal(true);
  };

  const openEditVisit = (visit) => {
    setEditVisit(visit);
    setShowNewRequestModal(true);
  };

  const handleNavigate = (page) => {
    if (page === 'newRequest') {
      openNewRequest();
      return;
    }
    setActivePage(page);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/login');
    }
  };

  const handleProfileFieldChange = (e) => {
    if (!isProfileEditMode) return;

    const { name, value } = e.target;

    if (name === 'pinCode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setProfileForm((prev) => ({ ...prev, pinCode: numericValue }));
      setProfilePincodeError('');
      setProfileDistrictName('');
      setProfileDistrictId(null);
      if (numericValue.length === 6) {
        validateProfilePincode(numericValue);
      }
      return;
    }

    if (name === 'emergencyContactPhoneNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setProfileForm((prev) => ({ ...prev, [name]: numericValue }));
      return;
    }

    if (name === 'age') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 3);
      setProfileForm((prev) => ({ ...prev, age: numericValue }));
      return;
    }

    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateProfilePincode = async (pincodeOverride) => {
    const pincode = (pincodeOverride ?? profileForm?.pinCode ?? '').trim();

    setProfilePincodeError('');

    if (!pincode) {
      setProfilePincodeError('Enter a 6-digit pincode.');
      return { ok: false, message: 'Enter a 6-digit pincode.' };
    }

    if (pincode.length !== 6) {
      setProfilePincodeError('Enter a 6-digit pincode.');
      return { ok: false, message: 'Enter a 6-digit pincode.' };
    }

    if (pincode === originalProfilePincode && (patientProfile?.districtID ?? patientProfile?.districtId)) {
      setProfileDistrictName(patientProfile.districtName || '');
      setProfileDistrictId(patientProfile.districtID ?? patientProfile.districtId ?? null);
      return { ok: true, message: '' };
    }

    setProfileDistrictName('');
    setProfileDistrictId(null);
    setIsValidatingProfilePincode(true);
    try {
      const response = await client.get(
        `/api/districts/validate-pincode?pincode=${pincode}`,
      );
      const result = response.data?.data;
      if (result?.districtName) {
        setProfileDistrictName(result.districtName);
        setProfileDistrictId(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }
      const message = 'Enter a valid pincode.';
      setProfilePincodeError(message);
      return { ok: false, message };
    } catch {
      const message = 'Unable to validate pincode. Please try again.';
      setProfilePincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingProfilePincode(false);
    }
  };

  const handleProfileCancel = () => {
    if (!patientProfile) return;

    if (
      !window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')
    ) {
      return;
    }

    setProfileForm(profileToForm(patientProfile));
    setProfileDistrictId(patientProfile.districtID ?? patientProfile.districtId ?? null);
    setProfileDistrictName(patientProfile.districtName || '');
    setOriginalProfilePincode(emptyIfPlaceholder(patientProfile.pinCode));
    setProfilePincodeError('');
    setIsProfileEditMode(false);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!isProfileEditMode) return;

    const patientId = patientProfile?.idPatient ?? patientProfile?.IdPatient;
    if (!profileForm || !patientId) return;

    if (profileForm.fullName.trim().length < 3) {
      toast.error('Full name must be at least 3 characters.');
      return;
    }

    const age = parseInt(profileForm.age, 10);
    if (!age || age < 1 || age > 120) {
      toast.error('Enter a valid age (1–120).');
      return;
    }

    if (!profileForm.gender) {
      toast.error('Please select gender.');
      return;
    }

    if (!profileForm.pinCode || profileForm.pinCode.length !== 6) {
      toast.error('Enter a valid 6-digit PIN code.');
      return;
    }

    if (profileForm.addressLine.trim().length < 5) {
      toast.error('Address must be at least 5 characters.');
      return;
    }

    const pincodeResult = await validateProfilePincode();
    if (!pincodeResult.ok) {
      toast.error(profilePincodeError || 'Enter a valid pincode.');
      return;
    }

    if (!profileDistrictId) {
      toast.error('Enter a valid pincode to load district.');
      return;
    }

    const payload = {
      IdPatient: patientId,
      FullName: profileForm.fullName.trim(),
      Age: age,
      Gender: profileForm.gender,
      MobileNumber: (patientProfile.mobileNumber || '').trim(),
      Email: (patientProfile.email || '').trim(),
      AddressLine: profileForm.addressLine.trim(),
      Landmark: profileForm.landmark.trim() || null,
      DistrictID: profileDistrictId,
      PINCode: profileForm.pinCode,
      EmergencyContactName: profileForm.emergencyContactName.trim(),
      EmergencyContactPhoneNumber: profileForm.emergencyContactPhoneNumber.trim(),
      KnownAllergies: profileForm.knownAllergies.trim(),
      MedicalHistory: profileForm.medicalHistory.trim(),
      CurrentMedications: profileForm.currentMedications.trim(),
    };

    setIsSavingProfile(true);
    try {
      const response = await client.put('/api/patients', payload);
      const { success, data, message } = response.data || {};

      if (!success || !data) {
        toast.error(message || 'Failed to update profile.');
        return;
      }

      setPatientProfile(data);
      setProfileForm(profileToForm(data));
      setProfileDistrictId(data.districtID ?? data.districtId ?? null);
      setProfileDistrictName(data.districtName || '');
      setOriginalProfilePincode(emptyIfPlaceholder(data.pinCode));
      setIsProfileEditMode(false);
      toast.success(message || 'Profile updated successfully.');
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(apiMessage || 'Failed to update profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const openViewVisit = (visit) => {
    setViewVisit(visit);
  };

  const openCancelVisit = (visit) => {
    setCancelVisit(visit);
  };

  const handleConfirmCancelVisit = async (cancelReason) => {
    if (!user?.patientId || !cancelVisit) return;

    setIsCancellingVisit(true);
    try {
      const response = await client.post('/api/visit-requests/cancel', {
        IdVisitRequest: cancelVisit.id,
        PatientID: user.patientId,
        CancelReason: cancelReason,
      });
      const { success, message } = response.data || {};
      if (!success) {
        toast.error(message || 'Unable to cancel visit request.');
        return;
      }
      toast.success(message || 'Visit request cancelled.');
      setCancelVisit(null);
      await loadVisits();
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(apiMessage || 'Unable to cancel visit request.');
    } finally {
      setIsCancellingVisit(false);
    }
  };

  const renderStatusPill = (visit) => (
    <span className={`patient-status-pill ${VISIT_STATUS_CLASS[visit.status] || ''}`}>
      {visit.statusLabel}
    </span>
  );

  const renderMyVisits = () => (
    <>
      <div className="patient-page-header">
        <div>
          <h1>My Visit Requests</h1>
          <p>All your consultation requests and their current status.</p>
        </div>
        <button type="button" className="btn login-submit-btn" onClick={openNewRequest}>
          + New Request
        </button>
      </div>

      <div className="patient-card">
        <div className="patient-filters row g-2 mb-3">
          <div className="col-md-8">
            <input
              type="text"
              className="form-control"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="cancelled">Cancelled</option>
              <option value="accepted">Accepted</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
              <option value="paid">Paid &amp; Closed</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover patient-table mb-0">
            <thead>
              <tr>
                <th>Service Type</th>
                <th>Date</th>
                <th>Time</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingVisits ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    Loading visit requests...
                  </td>
                </tr>
              ) : filteredVisits.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No visit requests found.
                  </td>
                </tr>
              ) : (
                filteredVisits.map((visit) => (
                  <tr key={visit.id}>
                    <td>{visit.serviceType}</td>
                    <td>{visit.preferredDate}</td>
                    <td>{visit.preferredTime}</td>
                    <td>{visit.amount}</td>
                    <td>{renderStatusPill(visit)}</td>
                    <td>
                      {visit.canEdit || visit.status === 'submitted' ? (
                        <div className="d-flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openEditVisit(visit)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => openCancelVisit(visit)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : visit.status === 'expired' ? (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={openNewRequest}
                        >
                          Re-request
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => openViewVisit(visit)}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderProfile = () => {
    if (isLoadingProfile) {
      return (
        <div className="patient-card text-center py-5 text-muted">Loading profile...</div>
      );
    }

    if (!patientProfile || !profileForm) {
      return (
        <div className="patient-card text-center py-5 text-muted">
          Profile not available. Please sign in again after registering.
        </div>
      );
    }

    const f = profileForm;
    const profileFieldsDisabled = !isProfileEditMode || isLoadingProfile || isSavingProfile;

    return (
      <>
        <div className="patient-page-header">
          <div>
            <h1>My Profile</h1>
            <p>
              {isProfileEditMode
                ? 'Update your profile information below.'
                : 'View your profile information. Click "Edit Profile" to make changes.'}
            </p>
          </div>
          {!isProfileEditMode && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsProfileEditMode(true)}
              disabled={isLoadingProfile}
            >
              Edit Profile
            </button>
          )}
        </div>
        <div className="patient-card">
          <form id="patient-profile-form" onSubmit={handleProfileSave}>
            <div className="row">
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-fullName" required>
                  Full Name
                </ProfileLabel>
                <input
                  type="text"
                  id="profile-fullName"
                  name="fullName"
                  className="form-control"
                  value={f.fullName}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                  required
                />
              </div>
              <div className="col-md-3 p-2">
                <ProfileLabel htmlFor="profile-age" required>
                  Age
                </ProfileLabel>
                <input
                  type="text"
                  id="profile-age"
                  name="age"
                  className="form-control"
                  value={f.age}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                  required
                />
              </div>
              <div className="col-md-3 p-2">
                <ProfileLabel htmlFor="profile-gender" required>
                  Gender
                </ProfileLabel>
                <select
                  id="profile-gender"
                  name="gender"
                  className="form-select"
                  value={f.gender}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-mobile" required>
                  Mobile Number
                </ProfileLabel>
                <input
                  type="tel"
                  id="profile-mobile"
                  name="mobileNumber"
                  className="form-control form-control-display-only"
                  value={f.mobileNumber}
                  readOnly
                />
              </div>
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-email" required>
                  Email Address
                </ProfileLabel>
                <input
                  type="email"
                  id="profile-email"
                  name="email"
                  className="form-control form-control-display-only"
                  value={f.email}
                  readOnly
                />
              </div>
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-pinCode" required>
                  PIN Code
                </ProfileLabel>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control w-25"
                    id="profile-pinCode"
                    name="pinCode"
                    value={f.pinCode}
                    onChange={handleProfileFieldChange}
                    onBlur={() => {
                      if (isProfileEditMode && (f.pinCode || '').length === 6) {
                        validateProfilePincode(f.pinCode);
                      }
                    }}
                    placeholder="e.g. 695001"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    disabled={profileFieldsDisabled}
                    required
                  />
                  <input
                    type="text"
                    className="form-control w-75 form-control-display-only"
                    value={profileDistrictName}
                    placeholder="District / Location"
                    readOnly
                    disabled
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </div>
                {profilePincodeError && (
                  <small className="form-text text-danger">{profilePincodeError}</small>
                )}
                {isValidatingProfilePincode && (
                  <small className="form-text text-muted">Validating pincode...</small>
                )}
              </div>
              <div className="col-12 p-2">
                <ProfileLabel htmlFor="profile-address" required>
                  Address
                </ProfileLabel>
                <textarea
                  id="profile-address"
                  name="addressLine"
                  className="form-control"
                  rows="2"
                  value={f.addressLine}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                  required
                />
              </div>
              <div className="col-12 p-2">
                <ProfileLabel htmlFor="profile-landmark">Landmark</ProfileLabel>
                <input
                  type="text"
                  id="profile-landmark"
                  name="landmark"
                  className="form-control"
                  value={f.landmark}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                />
              </div>
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-emergencyName">Emergency Contact Name</ProfileLabel>
                <input
                  type="text"
                  id="profile-emergencyName"
                  name="emergencyContactName"
                  className="form-control"
                  value={f.emergencyContactName}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                />
              </div>
              <div className="col-md-6 p-2">
                <ProfileLabel htmlFor="profile-emergencyPhone">Emergency Contact Phone</ProfileLabel>
                <input
                  type="tel"
                  id="profile-emergencyPhone"
                  name="emergencyContactPhoneNumber"
                  className="form-control"
                  value={f.emergencyContactPhoneNumber}
                  onChange={handleProfileFieldChange}
                  maxLength={10}
                  disabled={profileFieldsDisabled}
                />
              </div>
              <div className="col-12 p-2">
                <ProfileLabel htmlFor="profile-allergies">Known Allergies</ProfileLabel>
                <textarea
                  id="profile-allergies"
                  name="knownAllergies"
                  className="form-control"
                  rows="2"
                  value={f.knownAllergies}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                />
              </div>
              <div className="col-12 p-2">
                <ProfileLabel htmlFor="profile-medicalHistory">Medical History</ProfileLabel>
                <textarea
                  id="profile-medicalHistory"
                  name="medicalHistory"
                  className="form-control"
                  rows="2"
                  value={f.medicalHistory}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                />
              </div>
              <div className="col-12 p-2">
                <ProfileLabel htmlFor="profile-medications">Current Medications</ProfileLabel>
                <textarea
                  id="profile-medications"
                  name="currentMedications"
                  className="form-control"
                  rows="2"
                  value={f.currentMedications}
                  onChange={handleProfileFieldChange}
                  disabled={profileFieldsDisabled}
                />
              </div>

              {isProfileEditMode && (
                <div className="col-12 text-center mt-4">
                  <button
                    type="submit"
                    className="btn login-submit-btn px-5 me-2"
                    disabled={isSavingProfile || isValidatingProfilePincode}
                  >
                    {isSavingProfile ? 'Updating...' : 'Update Profile'}
                  </button>
                  <button
                    type="button"
                    className="btn cancel-btn px-5"
                    onClick={handleProfileCancel}
                    disabled={isSavingProfile}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </>
    );
  };

  const renderContent = () => {
    if (activePage === 'profile') return renderProfile();
    return renderMyVisits();
  };

  return (
    <div className="patient-portal">
      <PatientHeader
        userName={userName}
        onProfileClick={() => setActivePage('profile')}
        onLogout={handleLogout}
      />

      <div className="patient-layout">
        <PatientSidebar
          activePage={activePage === 'newRequest' ? 'newRequest' : activePage}
          visitCount={visits.length}
          onNavigate={handleNavigate}
          onNewRequest={openNewRequest}
          onLogout={handleLogout}
        />

        <main className="patient-main">{renderContent()}</main>
      </div>

      <NewRequestModal
        show={showNewRequestModal}
        patientId={user?.patientId}
        existingVisits={visits}
        editVisit={editVisit}
        onHide={() => {
          setShowNewRequestModal(false);
          setEditVisit(null);
          if (activePage === 'newRequest') setActivePage('myVisits');
        }}
        onSuccess={() => {
          setActivePage('myVisits');
          setEditVisit(null);
          loadVisits();
        }}
      />

      <VisitDetailsModal
        show={!!viewVisit}
        visit={viewVisit}
        onHide={() => setViewVisit(null)}
      />

      <CancelVisitModal
        show={!!cancelVisit}
        visit={cancelVisit}
        onHide={() => setCancelVisit(null)}
        onConfirm={handleConfirmCancelVisit}
        isSubmitting={isCancellingVisit}
      />
    </div>
  );
}

export default PatientDashboard;
